import { Transaction, generateCSV, downloadCSV } from './shared';

// Simple exchange rate cache implementation for category converter
const simpleExchangeRateCache = {
  getUniqueDates: (transactions: { date: string }[]) => {
    const dates = new Set();
    transactions.forEach(t => {
      if (t.date) dates.add(t.date);
    });
    return Array.from(dates) as string[];
  },
  
  getExchangeRatesForDates: async (dates: string[], targetCurrency: string) => {
    const ratesMap = new Map();
    
    for (const date of dates) {
      try {
        // Try the primary CDN first, fallback to Cloudflare
        let url = `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@${date}/v1/currencies/${targetCurrency.toLowerCase()}.json`;
        let response = await fetch(url);
        
        if (!response.ok) {
          console.log(`Primary API failed for ${date}, trying fallback`);
          // Fallback to Cloudflare
          url = `https://${date}.currency-api.pages.dev/v1/currencies/${targetCurrency.toLowerCase()}.json`;
          response = await fetch(url);
        }
        
        if (response.ok) {
          const data = await response.json();
          const rates = data[targetCurrency.toLowerCase()];
          if (rates) {
            ratesMap.set(date, rates);
          }
        }
      } catch (error) {
        console.error(`Failed to get rates for ${date}:`, error);
      }
    }
    
    return ratesMap;
  }
};

interface CategoryTransaction {
  Account: string;
  Date: string;
  Payee: string;
  Notes: string;
  Category: string;
  Amount: string;
}

interface ExchangeRates {
  [key: string]: number;
}

/**
 * Convert amount from source currency to target currency using exchange rates
 * exchangeRates is based on targetCurrency, so we need to use the inverse rate
 */
function convertCurrency(amount: number, fromCurrency: string, toCurrency: string, exchangeRates: ExchangeRates): number {
  if (fromCurrency.toLowerCase() === toCurrency.toLowerCase()) {
    return amount;
  }

  // API returns lowercase currency codes (e.g. "eur" not "EUR")
  const fromCurrencyLower = fromCurrency.toLowerCase();

  if (fromCurrencyLower in exchangeRates) {
    const rate = exchangeRates[fromCurrencyLower];
    if (rate > 0) {
      return amount / rate; // Inverse because rates are FROM target TO source
    }
  }

  console.warn(`No exchange rate found for ${fromCurrency} to ${toCurrency}, using amount as-is`);
  return amount;
}

/**
 * Parse date from various formats to ISO format (YYYY-MM-DD)
 */
function parseDate(dateStr: string): string {
  if (!dateStr) return '';
  
  try {
    const trimmedDateStr = dateStr.trim();
    
    // First, try common explicit patterns to avoid ambiguity
    // Priority 1: ISO format YYYY-MM-DD (already in correct format)
    const isoMatch = trimmedDateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      return trimmedDateStr; // Already in ISO format
    }
    
    // Priority 2: DD-MM-YYYY or DD/MM/YYYY (common international format)
    const ddmmyyyyMatch = trimmedDateStr.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (ddmmyyyyMatch) {
      const [, day, month, year] = ddmmyyyyMatch;
      const dayInt = parseInt(day);
      const monthInt = parseInt(month);
      
      // If day > 12, it's definitely dd-mm-yyyy format
      if (dayInt > 12) {
        const date = new Date(Date.UTC(parseInt(year), monthInt - 1, dayInt));
        return date.toISOString().split('T')[0];
      }
      
      // If month > 12, it's definitely mm-dd-yyyy format (though this is less common globally)
      if (monthInt > 12) {
        const date = new Date(Date.UTC(parseInt(year), dayInt - 1, monthInt));
        return date.toISOString().split('T')[0];
      }
      
      // For ambiguous cases (both day and month <= 12), default to dd-mm-yyyy
      // This is safer for international usage and prevents month/day flips
      const date = new Date(Date.UTC(parseInt(year), monthInt - 1, dayInt));
      return date.toISOString().split('T')[0];
    }
    
    // Priority 3: Try other date formats using explicit patterns
    // YYYY/MM/DD format
    const yyyymmddSlashMatch = trimmedDateStr.match(/^(\d{4})[/](\d{1,2})[/](\d{1,2})$/);
    if (yyyymmddSlashMatch) {
      const [, year, month, day] = yyyymmddSlashMatch;
      const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
      return date.toISOString().split('T')[0];
    }
    
    // Priority 4: Fallback to JavaScript's native parsing for other formats
    // Only use this as last resort to avoid mm/dd/yyyy assumption
    const date = new Date(trimmedDateStr);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date format: ${dateStr}`);
      return dateStr;
    }
    
    // Convert to ISO format YYYY-MM-DD
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.warn(`Error parsing date: ${dateStr}`, error);
    return dateStr;
  }
}

/**
 * Parse CSV content manually (browser-compatible)
 */
function parseCSV(csvText: string): CategoryTransaction[] {
  const lines = csvText.split('\n');
  if (lines.length < 2) return [];
  
  // Parse header row
  const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
  
  const transactions: CategoryTransaction[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    // Simple CSV parsing (handles quoted fields)
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim()); // Push the last value
    
    // Create transaction object
    const transaction: any = {};
    headers.forEach((header, index) => {
      transaction[header] = values[index] || '';
    });
    
    transactions.push(transaction as CategoryTransaction);
  }
  
  return transactions;
}

/**
 * Convert category-filtered CSV file to standardized format with currency conversion
 */
export async function convertCategoryCsv(
  file: File, 
  sourceCurrency: string, 
  targetCurrency: string,
  startDate: string,
  categorizeTransactions: boolean = false,
  budgetConfig?: {
    serverURL: string;
    password: string;
    budgetId: string;
    budgetPassword?: string;
  }
): Promise<string> {
  try {
    // Read file content
    const csvText = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
    
    // Parse CSV
    const allTransactions = parseCSV(csvText);
    console.log(`Parsed ${allTransactions.length} transactions from CSV`);
    
    // Filter transactions by category "Nourami" and start date
    const startDateObj = new Date(startDate);
    const filteredTransactions = allTransactions.filter(transaction => {
      const transactionDate = new Date(parseDate(transaction.Date));
      return transaction.Category === 'Nourami' && transactionDate >= startDateObj;
    });
    
    console.log(`Found ${filteredTransactions.length} 'Nourami' transactions from ${startDate} onwards`);
    
    if (filteredTransactions.length === 0) {
      throw new Error(`No transactions found with category 'Nourami' starting from ${startDate}`);
    }
    
    // Get unique dates from filtered transactions for exchange rate caching
    const transactionDates = filteredTransactions.map(t => ({ date: parseDate(t.Date) }));
            const uniqueDates = simpleExchangeRateCache.getUniqueDates(transactionDates);
    
    console.log(`Processing transactions across ${uniqueDates.length} unique dates`);
    
    // Fetch exchange rates for all unique dates (with caching)
            const exchangeRatesMap = await simpleExchangeRateCache.getExchangeRatesForDates(uniqueDates, targetCurrency);
    
    const transactions: Transaction[] = [];
    let skippedCount = 0;
    
    for (const row of filteredTransactions) {
      try {
        const amount = parseFloat(row.Amount);
        
        // Skip transactions with zero amounts
        if (amount === 0) {
          skippedCount++;
          continue;
        }
        
        // Parse date
        const isoDate = parseDate(row.Date);
        
        // Get exchange rates for this specific date
        const exchangeRates = exchangeRatesMap.get(isoDate);
        if (!exchangeRates) {
          console.warn(`No exchange rates found for date ${isoDate}, skipping transaction`);
          skippedCount++;
          continue;
        }
        
        // Convert amount from source currency to target currency using historical rates
        const convertedAmount = convertCurrency(Math.abs(amount), sourceCurrency, targetCurrency, exchangeRates);
        
        // Preserve original sign
        const finalAmount = amount < 0 ? -convertedAmount : convertedAmount;
        
        // Use payee field or fall back to account
        const payee = row.Payee?.trim() || row.Account?.trim() || '';
        
        // Create description from notes or use a default
        const description = row.Notes?.trim() || `${row.Category} transaction`;
        
        transactions.push({
          date: isoDate,
          description: description,
          amount: finalAmount.toFixed(2),
          payee: payee
        });
        
      } catch (error) {
        console.error(`Error processing row: ${row.Date} - ${row.Category}`, error);
        skippedCount++;
      }
    }
    
    // Generate CSV content using shared function
    const csvContent = await generateCSV(transactions, targetCurrency, false, categorizeTransactions, budgetConfig);
    
    console.log(`Successfully converted ${transactions.length} 'Nourami' transactions from ${sourceCurrency} to ${targetCurrency} (${skippedCount} skipped)`);
    return csvContent;
    
  } catch (error) {
    console.error('Error converting category CSV:', error);
    throw error;
  }
}

/**
 * Download a CSV file with default filename for category transactions
 */
export function downloadCategoryCSV(csvContent: string, filename: string = 'nimah_transactions.csv') {
  downloadCSV(csvContent, filename);
} 